import { useQuery } from '@apollo/client';
import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import classNames from 'classnames/bind';
import AirlineLogo from 'common/airlineLogo';
import Loading from 'common/loading';
import { DateFormat } from 'common/types';
import { days } from 'common/utils';
import { useInfoParamsContext } from 'contexts/infoParams';
import {
  getRecommendParams,
  useRecommendParamsContext,
} from 'contexts/recommendParams';
import { SeatClass } from 'contexts/searchParams';
import { useMemo } from 'react';
import styles from './index.module.scss';
import GET_RECOMMEND_BY_CITY from './recommendByCity.gql';

const cx = classNames.bind(styles);

export enum SortType {
  fareDesc = 'frd',
  sdateDesc = 'sdd',
}

interface IRecommendItinerary {
  airline: string;
  price: number;
  stops: number;
  sdate1: string;
  sdate2: string;
  roundTripTime: number;
}

interface IData {
  recommendByCity: IRecommendItinerary[];
}

interface IProps {
  sortOrder: SortType;
}

const compareFunctions = new Map([
  [
    SortType.fareDesc,
    (a: IRecommendItinerary, b: IRecommendItinerary) => a.price - b.price,
  ],
  [
    SortType.sdateDesc,
    (a: IRecommendItinerary, b: IRecommendItinerary) => {
      if (a.sdate1 < b.sdate1) return -1;
      if (a.sdate1 > b.sdate1) return 1;
      return 0;
    },
  ],
]);

export default function List({ sortOrder }: IProps) {
  const {
    infoSearchParams: {
      departureCity,
      arrivalCity,
      isDirect,
      isDomestic,
      period,
    },
  } = useInfoParamsContext();
  const { recommendSearchParams } = useRecommendParamsContext();
  const { continents, duration, fare, sdate, themes } = getRecommendParams(
    recommendSearchParams,
  );
  const { loading, error, data } = useQuery<IData>(GET_RECOMMEND_BY_CITY, {
    variables: {
      scity: departureCity.code,
      ecity: arrivalCity.code,
      roundTripTime: `${period},${period}`,
      stops: isDirect ? 0 : undefined,
      continents,
      duration,
      fare,
      sdate,
      themes,
    },
  });
  const sorted = useMemo(
    () =>
      [...(data?.recommendByCity ?? [])].sort(compareFunctions.get(sortOrder)),
    [data?.recommendByCity, sortOrder],
  );

  if (loading) {
    return (
      <div className={cx('loading')}>
        <Loading />
      </div>
    );
  }

  if (error)
    return (
      <div className={cx('error')}>
        <span className={cx('txt')}>
          서비스가 불안정합니다.
          <br />
          잠시 후 다시 접속 바랍니다.
          <br />
          이용에 불편을 드려 죄송합니다.
        </span>
      </div>
    );

  if (!data?.recommendByCity.length) {
    return (
      <div className={cx('error')}>
        <span className={cx('txt')}>
          선택하신 조건으로
          <br />
          검색된 추천 일정이 없습니다.
          <br />
        </span>
      </div>
    );
  }

  function getLandUrl(item: IRecommendItinerary) {
    const routes = `${departureCity.code}-${arrivalCity.code}-${item.sdate1}/${arrivalCity.code}-${departureCity.code}-${item.sdate2}`;
    if (isDomestic || (departureCity.isDomestic && arrivalCity.isDomestic)) {
      return `/flights/domestic/${routes}?adult=1&fareType=${SeatClass.YC}`;
    }
    return `/flights/international/${routes}?adult=1&fareType=${SeatClass.Y}${isDirect ? '&isDirect=true' : ''}`;
  }

  return (
    <ul className={cx('list')}>
      {sorted?.map((item, idx) => (
        <li key={idx} className={cx('item')}>
          <i className={cx('duration')}>{item.roundTripTime}일 일정</i>
          <b
            className={cx('path', {
              as_nonstop: item.stops === 0,
              as_1stop: item.stops === 1,
              as_2stop: item.stops === 2,
            })}
          >
            {item.stops === 0 ? '직항' : `경유${item.stops}회`}
          </b>
          <span className={cx('dates')}>
            <b className={cx('date')}>
              {days(item.sdate1).format(DateFormat.DotMDd)}
            </b>
            <b className={cx('date')}>
              {days(item.sdate2).format(DateFormat.DotMDd)}
            </b>
          </span>
          <AirlineLogo
            airlineCode={item.airline}
            className={cx('airline_logo')}
          />
          <strong className={cx('price')}>
            {withPriceSeperator(item.price)}
            원~
          </strong>
          <a
            className={cx('anchor')}
            href={getLandUrl(item)}
            data-event-nlog="click"
            data-event-area-code="rcd.schedule"
          />
        </li>
      ))}
    </ul>
  );
}
